import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal, X } from "lucide-react";
import { Seo } from "../components/Seo";
import { PublicationCard } from "../components/PublicationCard";
import { AiPublicationFinder } from "../components/AiPublicationFinder";
import { api } from "../lib/api";

export default function Publications() {
  const [params, setParams] = useSearchParams();
  const [facets, setFacets] = useState(null);
  const [data, setData] = useState({ items: [], total: 0, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState(params.get("search") || "");

  const filters = useMemo(() => ({ search: params.get("search") || "", year: params.get("year") || "", category: params.get("category") || "", disease_area: params.get("disease_area") || "", pub_type: params.get("pub_type") || "", page: Number(params.get("page") || 1) }), [params]);

  useEffect(() => { api.get("/publications/facets").then((r) => setFacets(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    setLoading(true);
    const clean = Object.fromEntries(Object.entries({ ...filters, limit: 25 }).filter(([, v]) => v));
    api.get("/publications", { params: clean }).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [filters]);

  const update = (patch) => {
    const next = { ...filters, ...patch, page: patch.page || 1 };
    setParams(Object.fromEntries(Object.entries(next).filter(([k, v]) => v && !(k === "page" && v === 1))));
  };
  const active = ["year", "category", "disease_area", "pub_type", "search"].filter((k) => filters[k]);

  const Select = ({ k, label, options, fmt = (v) => v }) => (
    <label className="flex flex-col gap-1">
      <span className="label !mb-0">{label}</span>
      <select data-testid={`pub-filter-${k}`} className="input !py-2" value={filters[k]} onChange={(e) => update({ [k]: e.target.value })}>
        <option value="">All</option>
        {options.map((o) => <option key={o.value} value={o.value}>{fmt(o.value)} ({o.count})</option>)}
      </select>
    </label>
  );

  return (
    <div data-testid="publications-page">
      <Seo title="Publications" description="Filterable archive of peer-reviewed publications, systematic reviews, meta-analyses and conference posters authored or supported by MarksMan Healthcare." jsonLd={data.items.length ? { "@context": "https://schema.org", "@type": "ItemList", name: "MarksMan Healthcare publications", numberOfItems: data.total, itemListElement: data.items.slice(0, 20).map((p, i) => ({ "@type": "ListItem", position: i + 1, item: { "@type": "ScholarlyArticle", headline: p.title, datePublished: p.year ? String(p.year) : undefined, url: p.external_url || undefined, publisher: p.citation || undefined, author: { "@type": "Organization", name: "MarksMan Healthcare" } } })) } : undefined} />
      <section className="border-b border-slate-200 bg-[var(--surface)]">
        <div className="mx-auto grid max-w-7xl gap-6 px-4 py-8 sm:px-6 lg:grid-cols-5 lg:items-start lg:gap-10 lg:px-8" data-testid="pub-header-grid">
          <div className="lg:col-span-2">
            <div className="eyebrow">Research archive</div>
            <h1 className="mt-2 text-4xl font-extrabold text-[var(--navy)] sm:text-5xl">Publications</h1>
            <p className="mt-3 text-base text-slate-600">Research publications in which our contribution was acknowledged — systematic reviews, meta-analyses, pharmacoeconomic analyses, RWE studies, guidelines and policy papers.</p>
            <form onSubmit={(e) => { e.preventDefault(); update({ search: query }); }} className="mt-4 flex gap-2">
              <div className="relative flex-1">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input data-testid="pub-search-input" className="input !pl-9" placeholder="Search titles, journals, conferences…" value={query} onChange={(e) => setQuery(e.target.value)} />
              </div>
              <button data-testid="pub-search-btn" className="btn-navy !py-2">Search</button>
            </form>
          </div>
          <div className="lg:col-span-3"><AiPublicationFinder /></div>
        </div>
      </section>

      <section className="mx-auto grid max-w-7xl gap-8 px-4 py-8 sm:px-6 lg:grid-cols-12 lg:px-8">
        <aside className="lg:col-span-3">
          <div className="sticky top-24 rounded-xl border border-slate-200 bg-[var(--surface)] p-5">
            <div className="flex items-center justify-between"><span className="flex items-center gap-2 text-sm font-semibold text-[var(--navy)]"><SlidersHorizontal size={15} /> Filters</span>{active.length > 0 && <button data-testid="pub-filter-clear" onClick={() => { setQuery(""); setParams({}); }} className="text-xs font-semibold text-[var(--teal)] hover:underline">Clear all</button>}</div>
            {facets && (
              <div className="mt-4 space-y-4">
                <Select k="year" label="Year" options={facets.years} />
                <Select k="pub_type" label="Type" options={facets.types} fmt={(v) => (v === "conference" ? "Conference poster" : "Journal article")} />
                <Select k="category" label="Category" options={facets.categories} />
                <Select k="disease_area" label="Disease area" options={facets.disease_areas} />
              </div>
            )}
          </div>
        </aside>
        <div className="lg:col-span-9">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 pb-3">
            <div data-testid="pub-results-count" className="font-mono text-xs uppercase tracking-widest text-slate-500">{loading ? "Loading…" : `${data.total} publications`}</div>
            <div className="flex flex-wrap gap-2">
              {active.map((k) => (
                <button key={k} data-testid={`pub-active-filter-${k}`} onClick={() => { if (k === "search") setQuery(""); update({ [k]: "" }); }} className="inline-flex items-center gap-1 rounded-full bg-[var(--navy)] px-3 py-1 text-xs font-medium text-white">{filters[k]} <X size={12} /></button>
              ))}
            </div>
          </div>
          {!loading && data.items.length === 0 && <div data-testid="pub-empty" className="py-16 text-center text-slate-500">No publications match these filters.</div>}
          <div>{data.items.map((p) => <PublicationCard key={p.id} pub={p} />)}</div>
          {data.pages > 1 && (
            <div className="mt-8 flex items-center justify-between">
              <button data-testid="pub-prev-page" disabled={filters.page <= 1} onClick={() => update({ page: filters.page - 1 })} className="btn-outline !py-2 disabled:opacity-40">Previous</button>
              <span className="font-mono text-xs text-slate-500">Page {filters.page} of {data.pages}</span>
              <button data-testid="pub-next-page" disabled={filters.page >= data.pages} onClick={() => update({ page: filters.page + 1 })} className="btn-outline !py-2 disabled:opacity-40">Next</button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
